import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Save } from "lucide-react";
import { createClientUser, listClientRoles } from "../../../api/chatbotApi";
import { accountAuthError } from "../../../api/accountAuthApi";
import useAuth from "../../../hooks/useAuth";
import { isClientAdmin, isPlatformAdmin } from "../../../util/accessControl";

const initialForm = {
  name: "",
  gender: "",
  email: "",
  role_ids: [],
  is_client_admin: false,
};

export default function ChatbotClientUserCreatePage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const authorized = isClientAdmin(user, clientId);

  React.useEffect(() => {
    let active = true;
    if (!authorized) return () => { active = false; };
    listClientRoles(clientId)
      .then((response) => {
        if (active) setRoles(Array.isArray(response?.data) ? response.data : []);
      }).catch((err) => { if (active) setError(err.response?.data?.detail || err.message); });
    return () => {
      active = false;
    };
  }, [authorized, clientId]);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setError("");

    setSaving(true);
    try {
      const result = await createClientUser({
        name: form.name.trim(),
        gender: form.gender.trim() || null,
        email: form.email.trim(),
        client_id: clientId,
        role_ids: form.role_ids,
        is_client_admin: isPlatformAdmin(user) && form.is_client_admin,
      });
      navigate(`/clients/${clientId}/users`, { state: {
        invitationNotice: result?.data?.invitation_status === "accepted"
          ? "User created. The invitation was accepted by the email provider."
          : "User created. The password setup invitation is queued for email delivery.",
      } });
    } catch (err) {
      setError(accountAuthError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!authorized) return <section className="rounded-xl bg-white p-8"><h1 className="text-xl font-bold">Access denied</h1><p className="mt-2 text-slate-600">Only Super Admins and Client Admins can manage client users.</p></section>;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to={`/clients/${clientId}/users`} className="inline-flex items-center gap-1 transition hover:text-slate-900">
          <ArrowLeft size={16} /> Team
        </Link>
        <span>/</span>
        <Link to={`/clients/${clientId}/users`} className="transition hover:text-slate-900">
          Client Users
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-900">New User</span>
      </div>

      <form onSubmit={handleCreate} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <div className="mb-6 border-b border-slate-100 pb-5">
          <h1 className="text-2xl font-bold text-slate-900">Add User</h1>
          <p className="mt-1 text-sm text-slate-500">Create a user and assign access to this client.</p>
        </div>

        {error && (
          <div className="mb-5 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <label htmlFor="new-user-name" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Name *</label>
            <input id="new-user-name" required type="text" value={form.name} onChange={updateField("name")} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none" autoFocus />
          </div>
          <div>
            <label htmlFor="new-user-gender" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Gender</label>
            <select id="new-user-gender" value={form.gender} onChange={updateField("gender")} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none">
              <option value="">Not specified</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="nonbinary">Non-binary</option>
              <option value="other">Other</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
          </div>
          <div>
            <label htmlFor="new-user-email" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Email *</label>
            <input id="new-user-email" required type="email" value={form.email} onChange={updateField("email")} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none" />
          </div>
          <fieldset className="md:col-span-2"><legend className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-700">Custom roles</legend>
            {roles.filter((role) => role.active !== false && !role.deleted).length === 0
              ? <p className="text-sm text-slate-500">No active custom roles; you can assign roles later.</p>
              : <div className="grid gap-2 sm:grid-cols-2">{roles.filter((role) => role.active !== false && !role.deleted).map((role) => <label key={role.id} className="flex items-center gap-2 rounded-lg border p-3 text-sm">
                <input type="checkbox" checked={form.role_ids.includes(role.id)} onChange={(event) => setForm((current) => ({ ...current, role_ids: event.target.checked ? [...current.role_ids, role.id] : current.role_ids.filter((id) => String(id) !== String(role.id)) }))} />
                <span><strong>{role.name}</strong><span className="ml-1 text-xs text-slate-500">{role.role_key}</span></span>
              </label>)}</div>}
          </fieldset>
          {isPlatformAdmin(user) && <label className="md:col-span-2 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
            <input type="checkbox" checked={form.is_client_admin} onChange={(event) => setForm((current) => ({ ...current, is_client_admin: event.target.checked }))} />
            <span><strong>Client Admin</strong><span className="block text-xs text-slate-600">Protected administrative access, separate from custom roles.</span></span>
          </label>}
          <p className="md:col-span-2 rounded-lg bg-cyan-50 p-4 text-sm text-cyan-900">An invitation with the login email, sign-in URL and secure password setup link will be emailed to this user. They must set their own password before using the application. No password is sent by email. After activation, they can enroll a WhatsApp login number in Account Security.</p>
        </div>

        <div className="mt-6 flex justify-end gap-3 border-t border-slate-100 pt-5">
          <Link to={`/clients/${clientId}/users`} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
            Cancel
          </Link>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb] disabled:opacity-50">
            <Save size={16} /> {saving ? "Creating..." : "Create User"}
          </button>
        </div>
      </form>
    </div>
  );
}
